import React, { useState, useEffect } from 'react';
import {
  Autocomplete, Alert, Button, Box, TextField, Grid, MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function ProduktFormular({
  initialValues,
  onSubmit,
  mode,
  produktNamen = [],
  kategorien,
  einheiten,
  orte,
  title
}) {
  const [form, setForm] = useState(initialValues);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [nameOptions, setNameOptions] = useState([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Setze Formulardaten, wenn initialValues sich ändern
  useEffect(() => {
    setForm(initialValues);
  }, [initialValues]);

  // Setze Default-Werte für Kategorie und Lagerort bei "add"
useEffect(() => {
  if (mode === "add" && kategorien.length > 0 && orte.length > 0) {
    setForm(prev => ({
      ...prev,
      kategorie_id: kategorien[0].id,
      lagerort_id: orte[0].id,
    }));
  }
}, [mode, kategorien, orte]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

const handleNameInputChange = (event, value, reason) => {
  setForm(prev => ({ ...prev, name: value }));

  if (value && value.length >= 2) {
    setNameLoading(true);
    fetch(`https://app.satoha.de/service/?modus=search&t=${encodeURIComponent(value)}`)
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (data.result || []);
        setNameOptions(arr);
        setNameLoading(false);
      })
      .catch(() => setNameLoading(false));
  } else {
    setNameOptions([]);
  }
};

  const handleSpeichern = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!form.verfallsdatum) {
        //setErrorMsg("Bitte ein Verfallsdatum auswählen.");
        setSuccessMsg(null);
        return; // Kein Speichern
    }
    
    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    const result = await onSubmit(form);
    if (setSuccessMsg && result && result.success) {
      setForm({
        name: '',
        menge: '',
        einheit_id: '',
        kategorie_id: kategorien[0]?.id || '',
        lagerort_id: orte[0]?.id || '',
        verfallsdatum: '',
      });
      setSuccessMsg(result.message || 'Gespeichert!');
      setShowValidation(false); // Reset Validation
    } else if (result && result.message) {
      setErrorMsg(result.message);
    }
    setSaving(false);
  };

  const handleAbbrechen = () => {
    navigate(-1); // Zurück zur vorherigen Seite
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }} component="form" onSubmit={handleSpeichern}>
      <h2>{title}</h2>
    {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
    {successMsg && <Alert severity="success">{successMsg}</Alert>}
    <Autocomplete
    freeSolo
    options={nameOptions}
    loading={nameLoading}
    getOptionLabel={option => typeof option === 'string' ? option : option.name}
    value={form.name || ''}
    inputValue={form.name || ''}
    onInputChange={(event, newInputValue, reason) => {
        // Nur bei Benutzereingabe suchen
        if (reason === 'input') {
        handleNameInputChange(event, newInputValue, reason);
        }
    }}
    onChange={(event, newValue) => {
        // newValue ist entweder ein String (neuer Name) oder ein Objekt (ausgewählter Vorschlag)
        if (typeof newValue === 'string') {
        setForm(prev => ({ ...prev, name: newValue }));
        } else if (newValue && typeof newValue === 'object') {
        setForm(prev => ({
            ...prev,
            name: newValue.name,
            menge: newValue.menge || '',
            einheit_id: newValue.einheit_id || '',
            gruppe_id: newValue.gruppe_id || '',
            kategorie_id: newValue.kategorie_id || '',
            // ggf. weitere Felder übernehmen
        }));
        } else {
        setForm(prev => ({ ...prev, name: '' }));
        }
    }}
    renderInput={(params) => (
        <TextField
        {...params}
        label="Name"
        name="name"
        fullWidth
        margin="normal"
    error={!form.name && showValidation}
    helperText={!form.name && showValidation ? "Bitte einen Namen eingeben." : ""}
        InputProps={{
            ...params.InputProps,
            endAdornment: (
            <>
                {nameLoading ? <span style={{ marginRight: 8 }}>Lädt...</span> : null}
                {params.InputProps.endAdornment}
            </>
            ),
        }}
        />
    )}
    />




      <Grid container spacing={2}>
        <Grid item xs={7}>
          <TextField
            label="Menge"
            name="menge"
            value={form.menge || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!form.menge && showValidation}
            helperText={!form.menge && showValidation ? "Bitte eine Menge auswählen." : ""}
          />
        </Grid>
        <Grid item xs={5}>
          <TextField
            select
            label="Einheit"
            name="einheit_id"
            value={form.einheit_id || ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!form.einheit_id && showValidation}
            helperText={!form.einheit_id && showValidation ? "Bitte eine Einheit auswählen." : ""}
          >
            {einheiten.map(einheit => (
              <MenuItem key={einheit.id} value={einheit.id}>
                {einheit.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <TextField
        label="Verfallsdatum"
        type="date"
        name="verfallsdatum"
        value={form.verfallsdatum || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        error={!form.verfallsdatum && showValidation}
        helperText={!form.verfallsdatum && showValidation ? "Bitte ein Verfallsdatum auswählen." : ""}
      />

      <TextField
        select
        label="Kategorie"
        name="kategorie_id"
        value={form.kategorie_id || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
      >
        {kategorien.map(kat => (
          <MenuItem key={kat.id} value={kat.id}>
            {kat.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Lagerort"
        name="lagerort_id"
        value={form.lagerort_id || form.ort_id || ''}
        onChange={handleChange}
        fullWidth
        margin="normal"
      >
        {orte.map(ort => (
          <MenuItem key={ort.id} value={ort.id}>
            {ort.name}
          </MenuItem>
        ))}
      </TextField>

      <Box mt={2}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mr: 1 }}
          disabled={saving}
        >
          {mode === 'edit' ? 'Speichern' : 'Hinzufügen'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAbbrechen}
          disabled={saving}
        >
          Abbrechen
        </Button>
      </Box>
    </Box>
  );
}
